import React, { useEffect, useState, useCallback, useMemo } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { useHistory } from "react-router-dom";

import { makeStyles, withStyles } from "@material-ui/core/styles";

import Container from "@material-ui/core/Container";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import Grid from "@material-ui/core/Grid";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import MenuItem from "@material-ui/core/MenuItem";

import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardBody from "components/Card/CardBody";
import CardText from "components/Card/CardText";

import EnhancedTableHead, {
  stableSort,
  getComparator
} from "../../common/EnhancedTableHead";
import Loading from "../../common/Loading";

import { actionCreators as actionCreatorsChapter } from "../../../_actions/chapterActions";
import { CHAPTER_FINANCIAL_STATUS, CHAPTER_STATUS } from "../../../constants";
import { PAGE_SIZE } from "../../../constants";
import { yearList } from "../../../utils";

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%"
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2)
  },
  table: {
    minWidth: 750
  },
  marginBottom: {
    marginBottom: theme.spacing(3)
  },
  cursorPointer: {
    cursor: "pointer"
  },
  fiscalYear: {
    minWidth: "120px"
  },
  expanded: {
    flex: 1
  }
}));

const StyledTableRow = withStyles(theme => ({
  root: {
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover
    }
  }
}))(TableRow);

const headCells = [
  {
    id: "year",
    numeric: false,
    disablePadding: false,
    label: "Year"
  },
  {
    id: "status",
    disablePadding: false,
    label: "Status"
  },
  {
    id: "updatedDate",
    disablePadding: false,
    label: "Updated Date"
  },
  {
    id: "updatedByUserName",
    disablePadding: false,
    label: "Updated By"
  }
];

const ChapterFinancials = props => {
  const classes = useStyles();
  const [reportStatus, setReportStatus] = useState(
    parseInt(localStorage.getItem("reportStatus")) ||
      CHAPTER_FINANCIAL_STATUS.NONE
  );
  const [fiscalYear, setFiscalYear] = useState(
    parseInt(localStorage.getItem("fiscalYear")) || -1
  );
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("year");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(
    parseInt(localStorage.getItem("chapterFinancialPageSize")) || PAGE_SIZE
  );
  const [currentFiscalYear, setCurrentFiscalYear] = useState(0);
  const history = useHistory();

  const {
    getChapterFinancials,
    getCurrentFiscalYear,
    chapterFinancials,
    resetIsFetched,
    isFetching,
    isFetched,
    user
  } = props;

  useEffect(() => {
    (async () => {
      const fiscalYear = await getCurrentFiscalYear();
      setCurrentFiscalYear(fiscalYear);

      if (!isFetching.CHAPTER_FINANCIALS && !isFetched.CHAPTER_FINANCIALS) {
        await getChapterFinancials();
      }
    })();
  }, []);

  const fullChapterList = useMemo(() => {
    if (!chapterFinancials) return [];

    const listedYears = chapterFinancials.map(row => row.year);
    const firstYear = Math.min(...listedYears);

    if (firstYear > currentFiscalYear) {
      return [];
    }

    return Array(currentFiscalYear - firstYear + 1)
      .fill()
      .map((item, index) => index + firstYear)
      .map(year => {
        const financial = chapterFinancials.find(x => x.year === year);

        if (!financial) {
          return {
            status: CHAPTER_FINANCIAL_STATUS.NOT_SUBMITTED,
            year,
            chapter: user.chapter,
            updatedDate: new Date().setFullYear(1900).toLocaleString(),
            updatedByUserName: "N/A"
          };
        }
        return {
          ...financial,
          chapterName: financial.chapter.name
        };
      })
      .filter(
        row =>
          (+reportStatus === CHAPTER_FINANCIAL_STATUS.NONE ||
            +reportStatus === row.status) &&
          (fiscalYear === row.year || fiscalYear === -1)
      );
  }, [chapterFinancials, currentFiscalYear, reportStatus, fiscalYear]);

  const handleReportStatus = useCallback(({ target: { value } }) => {
    setReportStatus(+value);
    localStorage.setItem("reportStatus", value);
  }, []);

  const handleFiscalYear = useCallback(({ target: { value } }) => {
    setFiscalYear(+value);
    localStorage.setItem("fiscalYear", value);
    setPage(0);
  }, []);

  const handleViewMailLog = useCallback(
    (id, event) => {
      event.stopPropagation();
      history.push(`/chapter/financial-report/${id}/mail-log`);
    },
    [history]
  );

  const handleViewReport = useCallback(
    row => () => {
      resetIsFetched();
      if (row.id === undefined) {
        history.push(
          `/chapter/financial-report/add?chapter=${user.chapter.id}&year=${row.year}`
        );
      } else {
        history.push(`/chapter/financial-report/${row.id}`);
      }
    },
    [history]
  );

  const handleRequestSort = useCallback(
    (event, property) => {
      const isAsc = orderBy === property && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
    },
    [orderBy, order]
  );

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback(({ target: { value } }) => {
    setRowsPerPage(parseInt(value, 10));
    localStorage.setItem("chapterFinancialPageSize", value);
    setPage(0);
  }, []);

  const paginate = useCallback(
    list => {
      return list.slice(page * rowsPerPage, (page + 1) * rowsPerPage) || [];
    },
    [page, rowsPerPage, reportStatus]
  );

  const getStandardDate = useCallback(dateString => {
    const date = new Date(dateString);
    var newString = date.toLocaleString("en-US");
    if (newString === "Invalid Date" || newString.indexOf("1969") > -1) {
      return "N/A";
    } else {
      return newString;
    }
  }, []);

  const getUpdatedByUserName = useCallback(userName => {
    if (userName === undefined || userName === null) {
      return "N/A";
    } else {
      return userName;
    }
  }, []);

  return (
    <Container>
      <Card>
        <CardHeader color="primary" text>
          <CardText color="primary">
            <Typography variant="h4">
              Chapter Financial Reports - {user.chapter.name}
            </Typography>
          </CardText>
        </CardHeader>
        <CardBody>
          <Grid container justify="flex-end" spacing={2}>
            <Grid item className={classes.marginBottom}>
              <FormControl variant="outlined" className={classes.fiscalYear}>
                <InputLabel id="report-status-select">Report Status</InputLabel>
                <Select
                  labelId="report-status-select"
                  value={reportStatus}
                  onChange={handleReportStatus}
                  label="Report Status"
                  MenuProps={{ classes: { paper: classes.menuPaper } }}
                >
                  <MenuItem value={CHAPTER_FINANCIAL_STATUS.NONE}>
                    None
                  </MenuItem>
                  {Object.keys(CHAPTER_STATUS).map(key => (
                    <MenuItem value={key} key={`rs-${key}`}>
                      {CHAPTER_STATUS[key]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item className={classes.marginBottom}>
              <FormControl variant="outlined" className={classes.fiscalYear}>
                <InputLabel id="fiscal-year-select">Fiscal Year</InputLabel>
                <Select
                  labelId="fiscal-year-select"
                  value={fiscalYear}
                  onChange={handleFiscalYear}
                  label="Fiscal Year"
                  MenuProps={{ classes: { paper: classes.menuPaper } }}
                >
                  <MenuItem value={-1}>All</MenuItem>
                  {yearList.map(value => (
                    <MenuItem value={value} key={`fiy-${value}`}>
                      {value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <TableContainer>
            <Table
              className={classes.table}
              aria-labelledby="tableTitle"
              size="medium"
              aria-label="enhanced table"
            >
              <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                rowCount={fullChapterList.length || 0}
                headCells={headCells}
              />
              <TableBody>
                {isFetching.CHAPTER_FINANCIALS || currentFiscalYear === 0 ? (
                  <StyledTableRow>
                    <TableCell colSpan={4} align="center">
                      <Loading />
                    </TableCell>
                  </StyledTableRow>
                ) : !fullChapterList || fullChapterList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No data
                    </TableCell>
                  </TableRow>
                ) : (
                  paginate(
                    stableSort(fullChapterList, getComparator(order, orderBy))
                  ).map((row, index) => {
                    const labelId = `enhanced-table-row-${index}`;

                    return (
                      <StyledTableRow
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={labelId}
                        className={classes.cursorPointer}
                        onClick={handleViewReport(row)}
                      >
                        <TableCell component="th" scope="row">
                          {row.year}
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {CHAPTER_STATUS[row.status]}
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {getStandardDate(row.updatedDate)}
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {getUpdatedByUserName(row.updatedByUserName)}
                        </TableCell>
                        <TableCell
                          component="th"
                          scope="row"
                          align="right"
                          onClick={
                            row.mailLog && row.mailLog.length > 0
                              ? e => handleViewMailLog(row.id, e)
                              : null
                          }
                        >
                          {row.mailLog && row.mailLog.length > 0
                            ? "Mail Log"
                            : ""}
                        </TableCell>
                      </StyledTableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={fullChapterList.length || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </CardBody>
      </Card>
    </Container>
  );
};

const mapStateToProps = state => ({
  chapterFinancials: state.chapterReducer.chapterFinancials,
  totalLength: state.chapterReducer.total,
  isFetching: state.chapterReducer.isFetching,
  isFetched: state.chapterReducer.isFetched,
  user: state.userReducer.user
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({ ...actionCreatorsChapter }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ChapterFinancials);
